import * as React from 'react';
import styled, { css } from 'styled-components';
import MDSpinner from 'react-md-spinner';

type ButtonVariant = 'outline' | 'solid' | 'underline';
type ButtonColorVariant = 'primary' | 'secondary' | 'tertiary';

interface IButton extends React.HTMLAttributes<HTMLButtonElement> {
  type?: any;
  variant: ButtonVariant;
  colorVariant: ButtonColorVariant;
  center?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  withIcon?: boolean;
  onClick?: (e: any) => void;
  target?: string;
}

export const Button: React.FC<IButton> = React.memo(
  ({
    type,
    variant,
    colorVariant,
    center,
    children,
    fullWidth,
    disabled,
    loading,
    withIcon,
    onClick,
  }) => {
    const buttonRef: any = React.useRef(null);
    if (variant === 'solid') {
      return (
        <StyledButton
          type={type}
          variant={variant}
          colorVariant={colorVariant}
          fullwidth={fullWidth?.toString()}
          disabled={disabled}
          onClick={onClick}
          ref={buttonRef}
          center={center?.toString()}
        >
          {loading ? (
            <>
              <MDSpinner size={20} singleColor="#000" />
            </>
          ) : (
            children
          )}
        </StyledButton>
      );
    } else if (variant === 'outline') {
      return (
        <StyledBorderButton
          type={type}
          variant={variant}
          colorVariant={colorVariant}
          fullwidth={fullWidth?.toString()}
          disabled={disabled}
          onClick={onClick}
          ref={buttonRef}
          center={center?.toString()}
        >
          <StyledSpan center={center?.toString()}>{children}</StyledSpan>
        </StyledBorderButton>
      );
    } else if (variant === 'underline') {
      return (
        <StyledLinkButton
          type={type}
          variant={variant}
          colorVariant={colorVariant}
          fullwidth={fullWidth?.toString()}
          disabled={disabled}
          onClick={onClick}
          ref={buttonRef}
          center={center?.toString()}
        >
          {children}
        </StyledLinkButton>
      );
    }
    return null;
  }
);

export const BaseButton = css<any>`
  display: block;
  transition: color 0.15s ease-in-out, background 0.15s ease-in-out;
  padding: 0 15px;
  width: 100%;
  height: 3rem;
  font-size: 16px;
  border: none;
  font-weight: 400;
  ${(props: any) => (props.fullwidth === 'true' ? `width: 100%;` : '')}
  appearance: none;
  -webkit-appearance: none;
  user-select: none;
  text-decoration: none;

  span {
    width: 100%;
  }

  &:hover {
    cursor: pointer;
  }

  &.full-width {
    @media screen and (max-width: 800px) {
      width: 100%;
    }
  }
`;

const PrimaryButton = css<any>`
  line-height: 3rem;
  background: ${({ theme, variant, colorVariant }) =>
    theme &&
    theme?.buttons &&
    theme?.buttons[variant] &&
    theme?.buttons[variant][colorVariant] &&
    theme?.buttons[variant][colorVariant].background};
  color: ${({ theme, variant, colorVariant }) =>
    theme &&
    theme?.buttons &&
    theme?.buttons[variant] &&
    theme?.buttons[variant][colorVariant] &&
    theme?.buttons[variant][colorVariant].color};
  border-radius: 0;

  ${({ center }) => {
    return center === 'true' ? 'text-align: center;' : '';
  }}
`;

const SecondaryButton = css<any>`
  line-height: 3rem;
  transition: border 0.15s ease-in-out;
  display: flex;
  align-items: center;
  background: transparent;
  color: ${({ theme, variant, colorVariant }) =>
    theme &&
    theme?.buttons &&
    theme?.buttons[variant] &&
    theme?.buttons[variant][colorVariant] &&
    theme?.buttons[variant][colorVariant].color};
  border: solid 2px
    ${({ theme, variant, colorVariant }) =>
      theme &&
      theme?.buttons &&
      theme?.buttons[variant] &&
      theme?.buttons[variant][colorVariant] &&
      theme?.buttons[variant][colorVariant].borderColor};

  ${({ center }) => {
    return center === 'true'
      ? 'justify-content: center;'
      : 'justify-content: space-between;';
  }}

  span {
    display: inline-block;
    line-height: 2.8125rem;
  }
`;

const TertiaryButton = css<any>`
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9375rem 0 0.625rem 0;
  background: none;

  ${({ center }) => {
    return center === 'true'
      ? 'justify-content: center;'
      : 'justify-content: space-between;';
  }}

  span {
    ${({ center }) => {
      return center === 'true' ? 'text-align: center;' : '';
    }}
  }
`;

const StyledButton: any = styled.button<any>`
  ${BaseButton}
  ${PrimaryButton}
`;

const StyledBorderButton: any = styled.button<any>`
  ${BaseButton}
  ${SecondaryButton}
`;

const StyledLinkButton: any = styled.a<any>`
  ${BaseButton}
  ${TertiaryButton}
  display: block;
`;

const StyledSpan = styled.span<any>`
  ${({ center }) => {
    return center === 'true' ? 'text-align: center;' : '';
  }}
`;
